import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, CalendarIcon } from '@heroicons/react/24/outline';

import { BillingGroupName, BillingPeriodUsage, BillingUsageGroup } from 'services/repositories/billing/billing';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';

import { UsageHistoryChart } from './UsageHistoryChart';

const _PERIOD_OPTIONS_MONTHS = [1, 3, 6, 12] as const;
export type PeriodMonths = (typeof _PERIOD_OPTIONS_MONTHS)[number];

type ActiveModulesSectionProps = {
  months: PeriodMonths;
  onMonthsChange: (months: PeriodMonths) => void;
  history: Array<BillingPeriodUsage>;
  usage: Array<BillingUsageGroup>;
  onGroupSelect: (groupName: BillingGroupName) => void;
};

export const ActiveModulesSection = ({
  months,
  onMonthsChange,
  history,
  usage,
  onGroupSelect,
}: ActiveModulesSectionProps) => {
  const { t } = useTranslation();

  const periodOptions = [
    { value: 1 as PeriodMonths, label: t('settings:usageAndModules.period.last1Month') },
    { value: 3 as PeriodMonths, label: t('settings:usageAndModules.period.last3Months') },
    { value: 6 as PeriodMonths, label: t('settings:usageAndModules.period.last6Months') },
    { value: 12 as PeriodMonths, label: t('settings:usageAndModules.period.last12Months') },
  ];

  return (
    <section className="grid grid-cols-[320px_1fr] gap-12">
      <div className="flex flex-col gap-2">
        <h3 className="text-base font-bold text-black">{t('settings:usageAndModules.activeModules.title')}</h3>
        <p className="text-sm text-gray-500">{t('settings:usageAndModules.activeModules.description')}</p>
      </div>
      <div className="flex flex-col gap-4">
        <Dropdown<PeriodMonths>
          value={months}
          onChange={(value) => value && onMonthsChange(value)}
          options={periodOptions}
          leftIcon={<CalendarIcon />}
        />

        <UsageHistoryChart history={history} />

        <div className="flex flex-col gap-2">
          {usage.map((group) => (
            <button
              type="button"
              key={group.group_name}
              onClick={() => onGroupSelect(group.group_name)}
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left transition hover:border-gray-300 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <span className="size-2 rounded-full bg-procuros-green-400 ring-4 ring-procuros-green-50" />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {t(`settings:usageAndModules.featureGroups.${group.group_name}`, {
                      defaultValue: group.display_name,
                    })}
                  </span>
                  {/* TODO: enable once the endpoint returns a per-group description
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="inline-flex">
                        <InformationCircleIcon className="size-4 text-gray-400" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(`settings:usageAndModules.featureGroups.${group.group_name}`)}
                    </TooltipContent>
                  </Tooltip>
                  */}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {t('settings:usageAndModules.activeModules.credits', {
                    count: Math.round(group.credits_used),
                  })}
                </span>
                <ArrowRightIcon className="size-5 text-gray-400" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
